import React from "react";
import {
  Typography,
  Chip,
  Avatar,
  Stack,
  Box,
  LinearProgress,
  CardHeader,
  CardContent,
} from "@mui/material";
import {
  FitnessCenter as TrainingIcon,
  CheckCircle as CompleteIcon,
  SkipNext as SkipIcon,
} from "@mui/icons-material";
import { ExerciseCard } from "./StyledComponents";
import SetDisplay from "./SetDisplay";
import { useExercisesBigQuery } from "../../../../../../hooks/useExercisesBigQuery";

// Component for displaying exercise details
const ExerciseDetail = ({ exercise }) => {
  // Fetch exercises from BigQuery for name lookups
  const { data: exercisesData, isLoading: exercisesLoading } =
    useExercisesBigQuery({
      search: "",
      type: "all",
      limit: 1000,
      offset: 0,
      enabled: true,
    });

  // Extract exercises array from BigQuery response
  const bigQueryExercises = exercisesData?.data?.exercises || [];

  // Helper function to get exercise name by ID from BigQuery data
  const getExerciseNameById = React.useMemo(() => {
    return (exerciseId) => {
      console.log("🔍 ExerciseDetail - Looking up exercise:", {
        exerciseId,
        bigQueryExercisesCount: bigQueryExercises.length,
        firstFewExercises: bigQueryExercises
          .slice(0, 3)
          .map((ex) => ({ id: ex.id, name: ex.name })),
        exercisesLoading,
      });

      if (!exerciseId) return "Unknown Exercise";

      // First try to find in BigQuery exercises
      const bigQueryExercise = bigQueryExercises.find(
        (ex) => ex.id === exerciseId,
      );
      if (bigQueryExercise) {
        console.log("✅ Found exercise in BigQuery:", bigQueryExercise.name);
        return bigQueryExercise.name;
      }

      console.log("❌ Exercise not found in BigQuery, using formatted name");
      // Last resort - format the ID as a readable name
      const formattedName = exerciseId
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      return formattedName;
    };
  }, [bigQueryExercises, exercisesLoading]);

  // Exclude skipped sets from completion calculations
  const nonSkippedSets = exercise.sets?.filter((set) => !(set.skipped === true)) || [];
  const completedSets =
    nonSkippedSets.filter((set) => set.completed).length || 0;
  const totalSets = nonSkippedSets.length || 0;
  const completionPercentage =
    totalSets > 0 ? (completedSets / totalSets) * 100 : 0;
  
  const isSkipped = exercise.skipped === true;

  // Get the exercise name using the helper function
  const exerciseName = React.useMemo(() => {
    console.log("🎯 ExerciseDetail - Getting exercise name for:", {
      exercise,
      exerciseName: exercise.exerciseName,
      exerciseField: exercise.exercise,
      exerciseIdField: exercise.exerciseId,
      idField: exercise.id,
      exercisesLoading,
    });

    if (exercisesLoading) return "Loading...";

    if (
      exercise.exerciseName &&
      !exercise.exerciseName.match(/^[A-Za-z0-9]{20,}$/)
    ) {
      console.log("✅ Using existing exercise name:", exercise.exerciseName);
      return exercise.exerciseName; // Use existing name if it looks like a real name (not an ID)
    }

    const exerciseId = exercise.exercise || exercise.exerciseId || exercise.id;
    console.log("🔍 Looking up exercise ID:", exerciseId);
    return getExerciseNameById(exerciseId);
  }, [exercise, exercisesLoading, getExerciseNameById]);

  return (
    <ExerciseCard>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: "primary.light" }}>
            <TrainingIcon fontSize="small" />
          </Avatar>
        }
        title={
          <Typography variant="h6" fontWeight="bold">
            {exerciseName}
          </Typography>
        }
        action={
          <Stack direction="row" spacing={1}>
            <Chip
              label={`${completedSets}/${totalSets} sets`}
              color={completedSets === totalSets ? "success" : "warning"}
              size="small"
            />
            {isSkipped && (
              <Chip
                label="Skipped"
                color="warning"
                icon={<SkipIcon />}
                size="small"
              />
            )}
            {exercise.swappedFromExerciseId && (
              <Chip
                label="Swapped"
                color="info"
                size="small"
              />
            )}
            {exercise.clientAdded === true && (
              <Chip label="Client Added" color="warning" size="small" />
            )}
            {exercise.completed && !isSkipped && (
              <Chip
                label="Complete"
                color="success"
                icon={<CompleteIcon />}
                size="small"
              />
            )}
          </Stack>
        }
      />

      <CardContent sx={{ pt: 0 }}>
        {exercise.swappedFromExerciseId && (
          <Box sx={{ mb: 2, p: 1, bgcolor: "success.50", borderRadius: 1, border: "1px solid", borderColor: "success.light" }}>
            <Typography variant="caption" color="success.main">
              Swapped from: <strong>{exercise.swappedFromExerciseName || getExerciseNameById(exercise.swappedFromExerciseId)}</strong>
            </Typography>
          </Box>
        )}
        {exercise.clientAdded === true && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "warning.50", borderRadius: 1, border: "1px solid", borderColor: "warning.light" }}>
            <Typography variant="caption" color="warning.dark">
              <strong>Client Added</strong> — This exercise was added by the client outside the planned workout
            </Typography>
          </Box>
        )}
        {/* Progress bar for overall exercise completion */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {completionPercentage.toFixed(0)}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "grey.200",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {/* Individual sets */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Sets Detail:
        </Typography>

        {exercise.sets?.map((set, index) => (
          <SetDisplay key={index} set={set} setNumber={index + 1} />
        ))}

        {exercise.notes && (
          <Box sx={{ mt: 2, p: 1, bgcolor: "info.50", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Notes: {exercise.notes}
            </Typography>
          </Box>
        )}
      </CardContent>
    </ExerciseCard>
  );
};

export default ExerciseDetail;
